/**
 * M.H.A. (Marta Hub Assistant) Configuration
 *
 * Agent for communication intelligence across all channels
 */

import type { AIAgent } from '../types.js';

export const MHA_AGENT_ID = 'mha';
export const MHA_AGENT_VERSION = '1.0.0';

export const MHA_AGENT: AIAgent = {
  id: MHA_AGENT_ID,
  name: 'Marta Hub Assistant',
  description: 'Manages communications, suggests follow-ups, drafts messages and analyzes contact patterns',
  version: MHA_AGENT_VERSION,
  enabled: true,

  capabilities: [
    {
      id: 'suggest-follow-up',
      name: 'Suggest Follow-up',
      description: 'Identifies contacts that need follow-up based on last interaction date',
      inputSchema: {
        type: 'object',
        properties: {
          daysInactive: { type: 'number', description: 'Days since last contact (default 14)' },
          limit: { type: 'number', description: 'Max suggestions (default 10)' },
        },
      },
      outputSchema: {
        type: 'object',
        properties: {
          contacts: { type: 'array', items: { type: 'object' } },
          count: { type: 'number' },
        },
      },
    },
    {
      id: 'analyze-communication',
      name: 'Analyze Communication Patterns',
      description: 'Analyzes frequency and channels of communication with a contact',
      inputSchema: {
        type: 'object',
        properties: {
          contactId: { type: 'string', description: 'Contact ID to analyze' },
        },
        required: ['contactId'],
      },
      outputSchema: {
        type: 'object',
        properties: {
          totalInteractions: { type: 'number' },
          channels: { type: 'object' },
          lastInteraction: { type: 'string' },
          frequency: { type: 'string' },
        },
      },
    },
    {
      id: 'draft-message',
      name: 'Draft Message',
      description: 'Drafts a message based on context and contact history',
      inputSchema: {
        type: 'object',
        properties: {
          contactId: { type: 'string', description: 'Contact ID' },
          channel: { type: 'string', enum: ['email', 'whatsapp', 'message', 'sms'] },
          intent: { type: 'string', description: 'What the message should communicate' },
        },
        required: ['intent'],
      },
      outputSchema: {
        type: 'object',
        properties: {
          draft: { type: 'string' },
          subject: { type: 'string' },
          tone: { type: 'string' },
        },
      },
    },
    {
      id: 'log-call-summary',
      name: 'Log Call Summary',
      description: 'Generates a structured summary from a call transcript or notes',
      inputSchema: {
        type: 'object',
        properties: {
          callId: { type: 'string' },
          transcript: { type: 'string' },
          notes: { type: 'string' },
        },
      },
      outputSchema: {
        type: 'object',
        properties: {
          summary: { type: 'string' },
          actionItems: { type: 'array', items: { type: 'string' } },
          sentiment: { type: 'string', enum: ['positive', 'neutral', 'negative'] },
        },
      },
    },
  ],

  workflows: ['follow-up-reminder', 'contact-enrichment', 'call-post-processing'],
  triggers: [
    'entity.created/message',
    'entity.created/call',
    'entity.created/email',
    'entity.updated/contact',
    'entity.created/videocall',
    'entity.updated/videocall',
  ],

  metrics: { totalCalls: 0, successRate: 0, avgLatencyMs: 0, correctionRate: 0 },
  trainingDataCount: 0,
  lastTrainedAt: undefined,
};

export const MHA_SETTINGS = {
  model: 'llama-3.3-70b-versatile',
  maxTokens: 400,
  followUpDefaultDays: 14,
  maxFollowUpSuggestions: 10,
  sentimentAnalysisEnabled: true,
};
